import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import { ExpandMore, Comment } from '@mui/icons-material';
import { format } from 'date-fns';
import { apiService } from '../../services/api';

interface ActionComment {
  historyId?: number;
  actionId: number;
  actionType: string;
  comments: string;
  commentedAt: string;
  commentedBy: {
    userId: number | null;
    email: string;
    displayName: string;
  } | null;
}

interface ActionCommentsProps {
  actionId: number | null | undefined;
  title?: string;
}

export function ActionComments({ actionId, title = 'Action comments' }: ActionCommentsProps) {
  const [comments, setComments] = useState<ActionComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!actionId) {
      setComments([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.getActionComments(actionId)
      .then((data) => {
        if (!cancelled) {
          setComments(data.comments || []);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load action comments';
          setError(message);
          setComments([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [actionId]);

  if (!actionId) return null;

  return (
    <Accordion expanded={expanded} onChange={(_, next) => setExpanded(next)} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Comment fontSize="small" />
          <Typography variant="subtitle2">{title}</Typography>
          {!loading && <Chip size="small" label={comments.length} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading comments...</Typography>
          </Box>
        )}
        {error && <Alert severity="warning">{error}</Alert>}
        {!loading && !error && comments.length === 0 && (
          <Typography variant="body2" color="text.secondary">No comments recorded for this action.</Typography>
        )}
        {!loading && comments.length > 0 && (
          <List dense>
            {comments.map((c, idx) => (
              <ListItem key={c.historyId ?? idx} alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  <Chip size="small" label={c.actionType} />
                  <Typography variant="caption" color="text.secondary">
                    {c.commentedBy?.displayName || c.commentedBy?.email || 'Unknown'}
                    {c.commentedAt ? ` · ${format(new Date(c.commentedAt), 'MMM d, yyyy h:mm a')}` : ''}
                  </Typography>
                </Box>
                <Typography variant="body2">{c.comments}</Typography>
              </ListItem>
            ))}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
